import { cn } from '@/utils/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-14 w-14 text-lg',
};

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  /** Small colored dot, e.g. for online/active status. */
  status?: 'online' | 'offline' | 'away';
  className?: string;
}

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

const STATUS_CLASSES: Record<NonNullable<AvatarProps['status']>, string> = {
  online: 'bg-success',
  offline: 'bg-text-muted',
  away: 'bg-warning',
};

export function Avatar({ name, src, size = 'md', status, className }: AvatarProps) {
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {src ? (
        <img
          src={src}
          alt={name}
          className={cn('rounded-full object-cover', SIZE_CLASSES[size])}
        />
      ) : (
        <span
          className={cn(
            'flex items-center justify-center rounded-full bg-primary-light font-medium text-primary',
            SIZE_CLASSES[size]
          )}
          aria-label={name}
        >
          {initialsFrom(name)}
        </span>
      )}
      {status && (
        <span
          className={cn('absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-white', STATUS_CLASSES[status])}
          aria-hidden="true"
        />
      )}
    </span>
  );
}
